<ui-section>
  <ui-block>
    <div class="platform-feature-flag-admin">
      <div class="page-header">
        <div>
          <h2>Platform Feature Flags</h2>
          <p class="page-subtitle">
            Author feature flags and rollout rules through the platform-owned administration workflow.
            Frontend checks remain UX gates only; server-side authorization stays authoritative.
          </p>
        </div>

        <div class="page-header__actions">
          <ui-button
            severity="secondary"
            [buttonText]="'Refresh'"
            [disabled]="isCatalogLoading() || isDefinitionLoading()"
            (click)="loadDefinitions(selectedFlagCode ?? undefined)"
          ></ui-button>
          <ui-button
            [buttonText]="'New Feature Flag'"
            [disabled]="!canAuthor() || isDefinitionSaving() || isRolloutSaving()"
            (click)="beginCreateDefinition()"
          ></ui-button>
        </div>
      </div>

      <p class="loading-text" *ngIf="isCatalogLoading()" role="status" aria-live="polite">
        Loading platform feature flags...
      </p>

      <ng-container *ngIf="!isCatalogLoading()">
        <div class="message-box success" *ngIf="successMessage()" role="status" aria-live="polite">
          {{ successMessage() }}
        </div>

        <div class="message-box error" *ngIf="errorState() as error" role="alert" aria-live="assertive">
          <p>{{ error.title }}</p>
          <ul>
            <li *ngFor="let message of error.messages">{{ message }}</li>
          </ul>

          <div class="error-meta" *ngIf="error.problemCode || error.issueCodes.length > 0 || error.correlationId">
            <span *ngIf="error.problemCode">Problem code: {{ error.problemCode }}</span>
            <span *ngIf="error.issueCodes.length > 0">Issue codes: {{ error.issueCodes.join(', ') }}</span>
            <span *ngIf="error.correlationId">Correlation ID: {{ error.correlationId }}</span>
          </div>
        </div>

        <div class="admin-shell">
          <aside class="catalog-panel" aria-label="Feature flag catalog">
            <div class="catalog-panel__header">
              <h3>Catalog</h3>
              <span>{{ definitions().length }} total</span>
            </div>

            <p class="empty-state" *ngIf="definitions().length === 0">
              No feature flags have been authored yet. Create the first feature flag to start rollout authoring.
            </p>

            <button
              *ngFor="let definition of definitions()"
              class="catalog-item"
              type="button"
              [class.catalog-item--active]="!isCreatingDefinition() && selectedFlagCode === definition.flagCode"
              (click)="selectDefinition(definition.flagCode)"
            >
              <span class="catalog-item__title">{{ definition.displayName }}</span>
              <span class="catalog-item__code">{{ definition.flagCode }}</span>
              <span class="catalog-item__meta">
                <span class="status-pill">{{ toStatusLabel(definition.statusCode) }}</span>
                <span>{{ definition.ownerCode }}</span>
              </span>
            </button>
          </aside>

          <section class="detail-panel" aria-label="Feature flag detail">
            <div class="detail-panel__header">
              <div>
                <h3>
                  {{ isCreatingDefinition() ? 'Create Feature Flag' : (selectedDefinition()?.displayName || 'Feature Flag Detail') }}
                </h3>
                <p class="detail-panel__subtitle">
                  {{ isCreatingDefinition()
                    ? 'Create a stable flag code before authoring rollout rows.'
                    : (selectedDefinition()?.flagCode || 'Select a feature flag from the catalog.') }}
                </p>
              </div>

              <div class="detail-panel__status" *ngIf="selectedDefinition() as definition">
                <span class="status-pill">{{ selectedDefinitionStatusLabel }}</span>
                <span>Owner: {{ definition.ownerCode }}</span>
                <span>Default: {{ definition.defaultEnabled ? 'Enabled' : 'Disabled' }}</span>
              </div>
            </div>

            <p class="loading-text" *ngIf="isDefinitionLoading()" role="status" aria-live="polite">
              Loading the selected feature flag...
            </p>

            <ng-container *ngIf="!isDefinitionLoading()">
              <div class="inline-note" *ngIf="definitionEditBlockedReason()">
                {{ definitionEditBlockedReason() }}
              </div>

              <ui-form
                #definitionForm
                [config]="definitionFormConfig"
                [(formValues)]="definitionFormValues"
                [disabled]="isDefinitionSaving()"
                [showFormErrors]="true"
              ></ui-form>

              <div class="form-actions">
                <ui-button
                  [buttonText]="isDefinitionSaving()
                    ? (isCreatingDefinition() ? 'Creating...' : 'Saving...')
                    : (isCreatingDefinition() ? 'Create Feature Flag' : 'Save Feature Flag')"
                  [disabled]="!canAuthor() || isDefinitionSaving() || (!!definitionEditBlockedReason())"
                  (click)="saveDefinition()"
                ></ui-button>

                <ui-button
                  severity="secondary"
                  [buttonText]="'Reset'"
                  [disabled]="isDefinitionSaving()"
                  (click)="resetDefinitionForm()"
                ></ui-button>

                <ui-button
                  *ngIf="canActivateDefinition()"
                  severity="secondary"
                  [buttonText]="isActionBusy('definition:activate') ? 'Activating...' : 'Activate'"
                  [disabled]="!canPublish() || isActionBusy('definition:activate') || isDefinitionSaving() || isRolloutSaving()"
                  (click)="activateSelectedDefinition()"
                ></ui-button>

                <ui-button
                  *ngIf="canDeactivateDefinition()"
                  severity="danger"
                  [buttonText]="isActionBusy('definition:deactivate') ? 'Deactivating...' : 'Deactivate'"
                  [disabled]="!canRollback() || isActionBusy('definition:deactivate') || isDefinitionSaving() || isRolloutSaving()"
                  (click)="deactivateSelectedDefinition()"
                ></ui-button>
              </div>

              <div class="rollout-section" *ngIf="selectedDefinition() as definition">
                <div class="rollout-section__header">
                  <div>
                    <h3>Rollout Authoring</h3>
                    <p>
                      Lifecycle state and effective dates come from the backend. Publish and rollback actions are applied to the authored rollout rows below.
                    </p>
                  </div>

                  <ui-button
                    [buttonText]="'New Rollout'"
                    [disabled]="!canAuthor() || !!rolloutEditBlockedReason() || isRolloutSaving()"
                    (click)="startCreateRollout()"
                  ></ui-button>
                </div>

                <div class="inline-note" *ngIf="rolloutEditBlockedReason()">
                  {{ rolloutEditBlockedReason() }}
                </div>

                <div class="rollout-grid" *ngIf="sortedRollouts().length > 0; else noRollouts">
                  <article class="rollout-card" *ngFor="let rollout of sortedRollouts()">
                    <div class="rollout-card__header">
                      <div>
                        <h4>Rollout #{{ rollout.id }}</h4>
                        <span class="status-pill">{{ toStatusLabel(rollout.statusCode) }}</span>
                      </div>

                      <div class="rollout-card__actions">
                        <ui-button
                          severity="secondary"
                          [buttonText]="'Edit'"
                          [disabled]="!canAuthor() || !canEditRollout(rollout) || isRolloutSaving() || isActionBusy('rollout:publish:' + rollout.id) || isActionBusy('rollout:rollback:' + rollout.id)"
                          (click)="editRollout(rollout)"
                        ></ui-button>
                        <ui-button
                          severity="secondary"
                          [buttonText]="isActionBusy('rollout:publish:' + rollout.id) ? 'Publishing...' : 'Publish'"
                          [disabled]="!canPublish() || !canPublishRollout(rollout) || isActionBusy('rollout:publish:' + rollout.id) || isRolloutSaving()"
                          (click)="publishRollout(rollout)"
                        ></ui-button>
                        <ui-button
                          severity="danger"
                          [buttonText]="isActionBusy('rollout:rollback:' + rollout.id) ? 'Rolling Back...' : 'Rollback'"
                          [disabled]="!canRollback() || !canRollbackPublishedRollout(rollout) || isActionBusy('rollout:rollback:' + rollout.id) || isRolloutSaving()"
                          (click)="rollbackRollout(rollout)"
                        ></ui-button>
                      </div>
                    </div>

                    <dl class="rollout-card__details">
                      <div>
                        <dt>Scope</dt>
                        <dd>
                          {{ rollout.scopeTypeCode }}
                          <span *ngIf="rollout.scopeKey"> / {{ rollout.scopeKey }}</span>
                        </dd>
                      </div>
                      <div>
                        <dt>Audience</dt>
                        <dd>{{ rollout.audienceCode || 'All audiences' }}</dd>
                      </div>
                      <div>
                        <dt>Environment</dt>
                        <dd>{{ rollout.environmentCode || 'All environments' }}</dd>
                      </div>
                      <div>
                        <dt>Enabled State</dt>
                        <dd>{{ toStatusLabel(rollout.enabledStateCode) }}</dd>
                      </div>
                      <div>
                        <dt>Precedence</dt>
                        <dd>{{ rollout.precedenceRank }}</dd>
                      </div>
                      <div>
                        <dt>Rollback Posture</dt>
                        <dd>{{ rollout.rollbackPostureCode }}</dd>
                      </div>
                      <div>
                        <dt>Effective Start</dt>
                        <dd>{{ rollout.effectiveStartUtc | date: 'medium' }}</dd>
                      </div>
                      <div>
                        <dt>Effective End</dt>
                        <dd>{{ rollout.effectiveEndUtc ? (rollout.effectiveEndUtc | date: 'medium') : 'Open ended' }}</dd>
                      </div>
                    </dl>
                  </article>
                </div>

                <ng-template #noRollouts>
                  <p class="empty-state">
                    No rollout rows have been authored for {{ definition.flagCode }} yet.
                  </p>
                </ng-template>

                <div class="rollout-editor">
                  <div class="rollout-editor__header">
                    <h4>{{ editingRolloutId() ? 'Edit Rollout' : 'Create Rollout' }}</h4>
                    <p>
                      Scope, audience, environment, precedence, effective dates, and rollback posture are all authored here without hand-editing the request payload.
                    </p>
                  </div>

                  <ui-form
                    #rolloutForm
                    [config]="rolloutFormConfig"
                    [(formValues)]="rolloutFormValues"
                    [disabled]="isRolloutSaving() || !!rolloutEditBlockedReason()"
                    [showFormErrors]="true"
                  ></ui-form>

                  <div class="form-actions">
                    <ui-button
                      [buttonText]="isRolloutSaving()
                        ? (editingRolloutId() ? 'Saving...' : 'Creating...')
                        : (editingRolloutId() ? 'Save Rollout' : 'Create Rollout')"
                      [disabled]="!canAuthor() || isRolloutSaving() || !!rolloutEditBlockedReason()"
                      (click)="saveRollout()"
                    ></ui-button>
                    <ui-button
                      severity="secondary"
                      [buttonText]="'Reset'"
                      [disabled]="isRolloutSaving()"
                      (click)="resetRolloutForm()"
                    ></ui-button>
                  </div>
                </div>
              </div>
            </ng-container>
          </section>
        </div>
      </ng-container>
    </div>
  </ui-block>
</ui-section>
